import {Alert, Button, Col, Container, FormSelect, InputGroup, Row, Spinner} from "react-bootstrap";
import {useEffect, useMemo, useRef, useState} from "react";
import ReactDatePicker from "react-datepicker";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCalendar} from "@fortawesome/free-solid-svg-icons";
import {
    collection,
    CollectionReference,
    getDocs,
    limit,
    orderBy,
    query,
    QueryConstraint,
    QueryDocumentSnapshot,
    startAfter,
    where,
} from "firebase/firestore";
import {appFirestore} from "../../../../index";
import RRTable from "../../../components/rr-table/RRTable";
import {dateTimeToString, dateToString} from "../../../tools";
import {useAppSelector} from "../../../hooks";
import {selectDepartment} from "../../../../features/AppStateSlice";
import {calendarChangeLogFBConverter} from "./CalendarChangeLogFBConverter";
import {
    CALENDAR_CHANGE_LOG_COLLECTION,
    CalendarChangeLogDocument,
    CalendarChangeLogType,
} from "./CalendarChangeLogType";
import {selectAllEmployees, selectAllEvents} from "../../../../features/AppDataSlice";

const PAGE_SIZE = 50;
type ChangeFilter = "ALL" | CalendarChangeLogType;

type Filters = {
    startDate: Date;
    endDate: Date;
    changeType: ChangeFilter;
    employeeId: string;
    eventNumber: string;
};

type ChangePage = {
    changes: CalendarChangeLogDocument[];
    lastDocument: QueryDocumentSnapshot<CalendarChangeLogDocument> | null;
    hasMore: boolean;
};

function startOfDay(date: Date): Date {
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);
    return result;
}

function endOfDay(date: Date): Date {
    const result = new Date(date);
    result.setHours(23, 59, 59, 999);
    return result;
}

function defaultFilters(): Filters {
    const endDate = startOfDay(new Date());
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 29);
    return {startDate, endDate, changeType: "ALL", employeeId: "", eventNumber: ""};
}

function departmentLabel(department: string): string {
    if (department === "montage") return "Монтаж";
    if (department === "catering") return "Кетъринг";
    if (department === "store") return "Кухня";
    return department;
}

function belongsToDepartment(
    department: string,
    item: {isMontage?: boolean; isCatering?: boolean; isStore?: boolean; isLogistics?: boolean}
): boolean {
    if (department === "montage") return item.isMontage === true;
    if (department === "catering") return item.isCatering === true;
    if (department === "store") return item.isStore === true;
    if (department === "logistics") return item.isLogistics === true;
    return false;
}

export function calendarChangeTypeLabel(changeType: CalendarChangeLogType): string {
    switch (changeType) {
        case "EVENT": return "Събитие";
        case "START_DATE": return "Начална дата / час";
        case "DAY_TYPE": return "Тип ден";
        case "DESCRIPTION": return "Бележки от планирането";
        case "IS_SUPERVISOR": return "Супервайзор";
        case "IS_WAREHOUSE": return "Супервайзор склад";
        case "IS_MONTAGE": return "Роля „Служител“";
        case "END_DATE": return "Крайна дата / час";
        case "REST_TIME": return "Почивка";
        case "BONUS": return "Бонус";
        case "FACE": return "Оценка";
        case "NOTES": return "Бележки";
        case "IS_CONFIRMED": return "Потвърждение";
        case "DELETE": return "Изтриване";
        case "LOCK": return "Заключване";
        case "UNLOCK": return "Отключване";
    }
}

function toChangeFilter(value: string): ChangeFilter {
    const values: ChangeFilter[] = [
        "ALL", "EVENT", "START_DATE", "DAY_TYPE", "DESCRIPTION", "IS_SUPERVISOR", "IS_WAREHOUSE",
        "IS_MONTAGE", "END_DATE", "REST_TIME", "BONUS", "FACE", "NOTES", "IS_CONFIRMED", "DELETE",
        "LOCK", "UNLOCK",
    ];
    return values.includes(value as ChangeFilter) ? value as ChangeFilter : "ALL";
}

function dateValue(value: Date | null): string {
    return value ? dateTimeToString(value) : "—";
}

function periodValue(change: CalendarChangeLogDocument): string {
    if (change.changeType === "LOCK" || change.changeType === "UNLOCK") {
        return dateToString(change.periodStart) || "—";
    }
    return `${dateValue(change.periodStart)} – ${dateValue(change.periodEnd)}`;
}

function constraintsFor(department: string, filters: Filters): QueryConstraint[] {
    const constraints: QueryConstraint[] = [
        where("department", "==", department),
        where("changedAt", ">=", startOfDay(filters.startDate)),
        where("changedAt", "<=", endOfDay(filters.endDate)),
    ];
    if (filters.changeType !== "ALL") constraints.push(where("changeType", "==", filters.changeType));
    if (filters.employeeId) constraints.push(where("employeeId", "==", filters.employeeId));
    if (filters.eventNumber) constraints.push(where("eventNumber", "==", filters.eventNumber));
    constraints.push(orderBy("changedAt", "desc"));
    return constraints;
}

async function loadPage(
    changeCollection: CollectionReference<CalendarChangeLogDocument>,
    department: string,
    filters: Filters,
    cursor: QueryDocumentSnapshot<CalendarChangeLogDocument> | null
): Promise<ChangePage> {
    const pageConstraints = cursor ? [startAfter(cursor)] : [];
    const snapshot = await getDocs(query(
        changeCollection,
        ...constraintsFor(department, filters),
        ...pageConstraints,
        limit(PAGE_SIZE)
    ));
    return {
        changes: snapshot.docs.map(value => value.data()),
        lastDocument: snapshot.docs[snapshot.docs.length - 1] ?? cursor,
        hasMore: snapshot.docs.length === PAGE_SIZE,
    };
}

export default function CalendarChangeLogReportView() {
    const department = useAppSelector(selectDepartment);
    const employees = useAppSelector(selectAllEmployees);
    const events = useAppSelector(selectAllEvents).data;
    const initialFilters = useMemo(defaultFilters, []);
    const [filters, setFilters] = useState<Filters>(initialFilters);
    const [appliedFilters, setAppliedFilters] = useState<Filters>(initialFilters);
    const [refreshCounter, setRefreshCounter] = useState(0);
    const [changes, setChanges] = useState<CalendarChangeLogDocument[]>([]);
    const [lastDocument, setLastDocument] = useState<QueryDocumentSnapshot<CalendarChangeLogDocument> | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const previousDepartment = useRef(department);
    const changeCollection = useMemo(() => collection(
        appFirestore,
        CALENDAR_CHANGE_LOG_COLLECTION
    ).withConverter(calendarChangeLogFBConverter), []);
    const employeeOptions = useMemo(() => {
        const options = new Map<string, string>();
        employees
            .filter(employee => employee.id && belongsToDepartment(department, employee))
            .forEach(employee => options.set(employee.id!, employee.name));
        changes.forEach(change => {
            if (change.employeeId && change.employeeName) {
                options.set(change.employeeId, change.employeeName);
            }
        });
        return Array.from(options, ([value, label]) => ({value, label}))
            .sort((left, right) => left.label.localeCompare(right.label, "bg"));
    }, [changes, department, employees]);
    const eventOptions = useMemo(() => {
        const options = new Map<string, string>();
        (events || [])
            .filter(event => event.eventNumber?.trim() && belongsToDepartment(department, event))
            .forEach(event => options.set(
                event.eventNumber.trim(),
                `[${event.eventNumber.trim()}] ${event.eventName || event.eventShortName || ""}`.trim()
            ));
        changes.forEach(change => {
            const eventNumber = change.eventNumber.trim();
            if (eventNumber) {
                options.set(eventNumber, `[${eventNumber}] ${change.eventName}`.trim());
            }
        });
        return Array.from(options, ([value, label]) => ({value, label}))
            .sort((left, right) => left.value.localeCompare(right.value, "bg", {numeric: true}));
    }, [changes, department, events]);

    useEffect(() => {
        if (previousDepartment.current === department) return;
        previousDepartment.current = department;
        setFilters(current => ({...current, employeeId: "", eventNumber: ""}));
        setAppliedFilters(current => ({...current, employeeId: "", eventNumber: ""}));
    }, [department]);

    useEffect(() => {
        let active = true;
        async function loadFirstPage(): Promise<void> {
            setLoading(true);
            setErrorMessage(null);
            try {
                const page = await loadPage(changeCollection, department, appliedFilters, null);
                if (!active) return;
                setChanges(page.changes);
                setLastDocument(page.lastDocument);
                setHasMore(page.hasMore);
            } catch (error) {
                if (active) setErrorMessage(error instanceof Error ? error.message : "Неуспешно зареждане на лога.");
            } finally {
                if (active) setLoading(false);
            }
        }
        void loadFirstPage();
        return () => { active = false; };
    }, [appliedFilters, changeCollection, department, refreshCounter]);

    async function loadMore(): Promise<void> {
        if (!lastDocument || loadingMore) return;
        setLoadingMore(true);
        setErrorMessage(null);
        try {
            const page = await loadPage(changeCollection, department, appliedFilters, lastDocument);
            setChanges(current => [...current, ...page.changes]);
            setLastDocument(page.lastDocument);
            setHasMore(page.hasMore);
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : "Неуспешно зареждане на следващата страница.");
        } finally {
            setLoadingMore(false);
        }
    }

    function applyFilters(): void {
        if (filters.startDate > filters.endDate) {
            alert("Началната дата не може да е след крайната дата.");
            return;
        }
        setAppliedFilters({...filters});
        setRefreshCounter(value => value + 1);
    }

    return <Container fluid>
        <h1>Лог промени в {departmentLabel(department)}</h1>
        <Row className="g-2 mt-3 flex-nowrap">
            <Col>
                <Row className="lh-2-5em flex-wrap">
                    <Col xs="auto"><Row><Col xs="auto" className="lh-2-5em">Начална дата:</Col><Col xs="auto">
                        <InputGroup className="mb-2 w-180px flex-nowrap">
                            <ReactDatePicker className="form-control" dateFormat="dd.MM.yyyy" id="calendarLogFrom"
                                maxDate={filters.endDate} selected={filters.startDate} value={dateToString(filters.startDate)}
                                onChange={(date: Date | null) => date && setFilters({...filters, startDate: date})}/>
                            <label htmlFor="calendarLogFrom"><InputGroup.Text><FontAwesomeIcon icon={faCalendar} size="xl"/></InputGroup.Text></label>
                        </InputGroup>
                    </Col></Row></Col>
                    <Col xs="auto"><Row><Col xs="auto" className="lh-2-5em">Крайна дата:</Col><Col xs="auto">
                        <InputGroup className="mb-2 w-180px flex-nowrap">
                            <ReactDatePicker className="form-control" dateFormat="dd.MM.yyyy" id="calendarLogTo"
                                minDate={filters.startDate} selected={filters.endDate} value={dateToString(filters.endDate)}
                                onChange={(date: Date | null) => date && setFilters({...filters, endDate: date})}/>
                            <label htmlFor="calendarLogTo"><InputGroup.Text><FontAwesomeIcon icon={faCalendar} size="xl"/></InputGroup.Text></label>
                        </InputGroup>
                    </Col></Row></Col>
                    <Col xs="auto"><Row><Col xs="auto" className="lh-2-5em">Променено:</Col><Col xs="auto">
                        <FormSelect className="mb-2" value={filters.changeType}
                            onChange={event => setFilters({...filters, changeType: toChangeFilter(event.target.value)})}>
                            <option value="ALL">Всички</option>
                            {(["EVENT", "START_DATE", "DAY_TYPE", "DESCRIPTION", "IS_SUPERVISOR", "IS_WAREHOUSE",
                                "IS_MONTAGE", "END_DATE", "REST_TIME", "BONUS", "FACE", "NOTES", "IS_CONFIRMED",
                                "DELETE", "LOCK", "UNLOCK"] as CalendarChangeLogType[]).map(type =>
                                <option key={type} value={type}>{calendarChangeTypeLabel(type)}</option>)}
                        </FormSelect>
                    </Col></Row></Col>
                    <Col xs="auto"><Row><Col xs="auto" className="lh-2-5em">Служител:</Col><Col xs="auto">
                        <FormSelect className="mb-2" value={filters.employeeId}
                            onChange={event => setFilters({...filters, employeeId: event.target.value})}>
                            <option value="">Всички служители</option>
                            {employeeOptions.map(option =>
                                <option key={option.value} value={option.value}>{option.label}</option>)}
                        </FormSelect>
                    </Col></Row></Col>
                    <Col xs="auto"><Row><Col xs="auto" className="lh-2-5em">Събитие:</Col><Col xs="auto">
                        <FormSelect className="mb-2" value={filters.eventNumber}
                            onChange={event => setFilters({...filters, eventNumber: event.target.value})}>
                            <option value="">Всички събития</option>
                            {eventOptions.map(option =>
                                <option key={option.value} value={option.value}>{option.label}</option>)}
                        </FormSelect>
                    </Col></Row></Col>
                    <Col xs="auto"><Button onClick={applyFilters}>Покажи</Button></Col>
                </Row>
            </Col>
        </Row>

        {loading && <Spinner animation="border"/>}
        {errorMessage && <Alert variant="danger">Грешка при зареждане на лога: {errorMessage}</Alert>}
        {!loading && !errorMessage && <>
            <RRTable><thead><tr>
                <th>Дата на промяната</th><th>Променено</th><th>№ събитие</th><th>Служител</th>
                <th>Период</th><th>Преди</th><th>След</th><th>Променил</th>
            </tr></thead><tbody>
                {changes.length === 0 && <tr><td className="text-center" colSpan={8}>Няма промени за избрания период.</td></tr>}
                {changes.map(change => <tr key={change.id}>
                    <td>{dateTimeToString(change.changedAt)}</td>
                    <td>{calendarChangeTypeLabel(change.changeType)}</td>
                    <td>{change.eventNumber || "—"}</td>
                    <td>{change.employeeName || "—"}</td>
                    <td>{periodValue(change)}</td>
                    <td>{change.previousValue}</td><td>{change.newValue}</td><td>{change.changedByName}</td>
                </tr>)}
            </tbody></RRTable>
            <div className="d-flex align-items-center gap-2 mt-3">
                <span>Показани записи: {changes.length}</span>
                {hasMore && <Button disabled={loadingMore} onClick={() => void loadMore()} variant="outline-primary">
                    {loadingMore ? <Spinner animation="border" size="sm"/> : "Зареди още"}
                </Button>}
            </div>
        </>}
    </Container>;
}
